import { apiGetSubscription } from 'api/users/subscription';
import { useQuery, useQueryClient } from 'react-query';
import { queryKeys } from 'utils/react-query';
import { useCallback, useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { ReactLeft } from 'utils/uiStates/uiStates';
import { useUserSubscriptionMutation } from './useUserSubscriptionMutation';
import { usePreAuthCardModal } from './usePreAuthCardModal';
import { SubscriptionPlan } from '../Plans/constant';

export const useSubscriptionPlan = () => {
  const [upgradingPlanId, setUpgradingPlanId] = useState<string | null>(null);
  const userId = useCurrentUserId();
  const queryClient = useQueryClient();
  const { data: subscription, refetch, isLoading } = useQuery([queryKeys.UserSubscription, userId], () => apiGetSubscription({ userId }));
  const preAuthModal = usePreAuthCardModal({
    onSuccess: (plan: SubscriptionPlan) => {
      planMutation.mutate({ plan: { id: plan.id as string } });
    },
  });

  const handleSuccess = useCallback(
    (message = 'Subscription updated!') =>
      (data: CF.API.Users.SubscriptionResponse) => {
        refetch();
        track('Billing Page -> Subscription Plan Updated', { message, plan: data.plan });
        cogoToast.success(message);
        setUpgradingPlanId(null);
        queryClient.invalidateQueries([queryKeys.UserSubscription, userId]);
      },
    [cogoToast, userId, queryClient],
  );

  const handleFailure = useCallback(
    (defaultMessage = 'Error.') =>
      (error: ReactLeft) => {
        track('Billing Page -> Subscription Plan Update Error', { message: error.props?.reason });
        cogoToast.error(error.props?.reason, { heading: defaultMessage });
        setUpgradingPlanId(null);
      },
    [cogoToast],
  );

  const planMutation = useUserSubscriptionMutation({ userId, onSuccess: handleSuccess('Plan updated!'), onError: handleFailure() });

  const changeSubscription = (plan: SubscriptionPlan) => {
    setUpgradingPlanId(plan.id);
    preAuthModal(plan);
  };

  return {
    subscription,
    changeSubscription,
    isLoading,
    isUpgrading: planMutation.isLoading,
    isError: planMutation.isError,
    error: planMutation.error,
    isSuccess: planMutation.isSuccess,
    reset: planMutation.reset,
    upgradingPlanId,
  };
};
